if (!window.Eurus.loadedScript.has('deferred-alpine-section.js')) {
  window.Eurus.loadedScript.add('deferred-alpine-section.js');

  document.addEventListener('alpine:initialized', () => {
    const selector = '[data-deferred-alpine-section], [data-deferred-alpine-card]';
    const defaultRootMargin = '1000px 0px';
    const intersectionObservers = new Map();
    const dependencyPromises = new Map();

    const getIntersectionObserver = (element) => {
      if (!('IntersectionObserver' in window)) return null;

      const rootMargin = element.dataset.deferredAlpineRootMargin || defaultRootMargin;
      if (intersectionObservers.has(rootMargin)) return intersectionObservers.get(rootMargin);

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) hydrate(entry.target);
        });
      }, { rootMargin });

      intersectionObservers.set(rootMargin, observer);
      return observer;
    };

    const loadDependency = (url) => {
      const assetName = new URL(url, window.location.href).pathname.split('/').pop();
      if (window.Eurus.loadedScript.has(assetName)) return Promise.resolve();
      if (dependencyPromises.has(url)) return dependencyPromises.get(url);

      const existingScript = Array.from(document.scripts).find((script) => {
        if (!script.src) return false;
        return new URL(script.src).pathname === new URL(url, window.location.href).pathname;
      });

      const promise = new Promise((resolve, reject) => {
        const script = existingScript || document.createElement('script');
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', reject, { once: true });

        if (!existingScript) {
          script.src = url;
          script.dataset.deferredAlpineDependency = assetName;
          document.head.appendChild(script);
        }
      });

      dependencyPromises.set(url, promise);
      return promise;
    };

    const loadDependencies = (element) => {
      const value = element.dataset.deferredAlpineDependencies;
      if (!value) return Promise.resolve();

      try {
        return Promise.all(JSON.parse(value).map(loadDependency));
      } catch (error) {
        return Promise.reject(error);
      }
    };

    const hydrate = async (element) => {
      if (!element || element.hasAttribute('data-alpine-hydrated') || element.hasAttribute('data-alpine-hydrating')) return;

      element.setAttribute('data-alpine-hydrating', '');
      intersectionObservers.forEach((observer) => observer.unobserve(element));

      try {
        await loadDependencies(element);
      } catch (error) {
        console.error('Deferred section dependencies failed to load.', error);
        element.removeAttribute('data-alpine-hydrating');
        intersectionObserver?.observe(element);
        return;
      }

      element.removeAttribute('data-alpine-hydrating');
      element.setAttribute('data-alpine-hydrated', '');

      const initialize = () => {
        element.removeAttribute('x-ignore');

        // x-ignore also sets internal flags. Clear both before initializing the
        // complete section tree so nested product-card controls are included.
        delete element._x_ignore;
        delete element._x_ignoreSelf;
        Alpine.initTree(element);
      };

      if (typeof Alpine.mutateDom === 'function') {
        Alpine.mutateDom(initialize);
      } else {
        initialize();
      }
    };

    const observe = (root) => {
      if (!(root instanceof Element)) return;

      const elements = root.matches(selector)
        ? [root]
        : root.querySelectorAll(selector);

      elements.forEach((element) => {
        if (element.hasAttribute('data-alpine-hydrated')) return;

        const intersectionObserver = getIntersectionObserver(element);
        if (intersectionObserver) {
          intersectionObserver.observe(element);
        } else {
          hydrate(element);
        }
      });
    };

    document.querySelectorAll(selector).forEach(observe);

    const hydrateFromInteraction = (event) => {
      hydrate(event.target.closest?.(selector));
    };

    document.addEventListener('pointerdown', hydrateFromInteraction, true);
    document.addEventListener('focusin', hydrateFromInteraction, true);

    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach(observe);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }, { once: true });
}
